import { ActionIcon, Group, NumberInput, Stack } from '@mantine/core';
import {
	useGetAiTokenAutoTopupSettings,
	useSetAiTokenAutoTopupSettings,
} from '@repo/api-codegen';
import { SettingsButton } from '@repo/common/components';
import { ResourceIcon, Switch, Text } from '@repo/foundations';
import { useCallback, useState } from 'react';
import { openModal } from '../../components/ModalManager';
import { BillingInfoBanner } from '../BudgetSettings/components/BillingInfoBanner';
import { AUTO_RELOAD_DEFAULTS, AUTO_RELOAD_LIMITS } from './constants';

interface EditReloadSettingsModalProps {
	onSuccess?: () => void;
}

function EditReloadSettingsModal({ onSuccess }: EditReloadSettingsModalProps) {
	const { data: autoTopupSettings, refetch: refetchAutoTopupSettings } =
		useGetAiTokenAutoTopupSettings({});
	const { mutate: setAutoTopupSettings } = useSetAiTokenAutoTopupSettings({
		onSuccess: () => {
			refetchAutoTopupSettings();
			onSuccess?.();
		},
	});

	const [thresholdError, setThresholdError] = useState<string | null>(null);
	const [amountError, setAmountError] = useState<string | null>(null);

	const validateThreshold = (value: number | ''): string | null => {
		if (value === '' || value === null || value === undefined) {
			return 'Threshold is required';
		}
		if (value < AUTO_RELOAD_LIMITS.MIN) {
			return `Threshold must be at least $${AUTO_RELOAD_LIMITS.MIN}`;
		}
		if (value > AUTO_RELOAD_LIMITS.MAX) {
			return `Threshold cannot exceed $${AUTO_RELOAD_LIMITS.MAX}`;
		}
		return null;
	};

	const validateAmount = (value: number | ''): string | null => {
		if (value === '' || value === null || value === undefined) {
			return 'Amount is required';
		}
		if (value < AUTO_RELOAD_LIMITS.MIN) {
			return `Amount must be at least $${AUTO_RELOAD_LIMITS.MIN}`;
		}
		if (value > AUTO_RELOAD_LIMITS.MAX) {
			return `Amount cannot exceed $${AUTO_RELOAD_LIMITS.MAX}`;
		}
		return null;
	};

	const handleSave = (threshold: number, amount: number) => {
		const thresholdValidation = validateThreshold(threshold);
		const amountValidation = validateAmount(amount);

		setThresholdError(thresholdValidation);
		setAmountError(amountValidation);

		if (thresholdValidation || amountValidation) {
			return;
		}

		setAutoTopupSettings({
			body: {
				enabled: true,
				threshold,
				amount,
			},
		});
	};

	return (
		<Stack spacing="md">
			<Text size="sm">
				Configure auto-reload settings to avoid service disruptions when your
				balance reaches a specified minimum
			</Text>
			<NumberInput
				label="When credit balance reaches:"
				placeholder={`$${AUTO_RELOAD_DEFAULTS.THRESHOLD}`}
				value={autoTopupSettings?.threshold || AUTO_RELOAD_DEFAULTS.THRESHOLD}
				onChange={(value) => {
					const threshold = value || AUTO_RELOAD_DEFAULTS.THRESHOLD;
					const amount =
						autoTopupSettings?.amount || AUTO_RELOAD_DEFAULTS.AMOUNT;
					const error = validateThreshold(value);
					setThresholdError(error);
					if (!error) {
						handleSave(threshold, amount);
					}
				}}
				min={AUTO_RELOAD_LIMITS.MIN}
				max={AUTO_RELOAD_LIMITS.MAX}
				step={AUTO_RELOAD_LIMITS.STEP}
				error={thresholdError}
			/>
			<Text size="xs" c="dimmed">
				Minimum ${AUTO_RELOAD_LIMITS.MIN}
			</Text>
			<NumberInput
				label="Bring balance to:"
				placeholder={`$${AUTO_RELOAD_DEFAULTS.AMOUNT}`}
				value={Math.max(
					autoTopupSettings?.amount || AUTO_RELOAD_DEFAULTS.AMOUNT,
					(autoTopupSettings?.threshold ?? AUTO_RELOAD_LIMITS.MIN) +
						AUTO_RELOAD_LIMITS.STEP
				)}
				onChange={(value) => {
					const amount = value || AUTO_RELOAD_DEFAULTS.AMOUNT;
					const threshold =
						autoTopupSettings?.threshold || AUTO_RELOAD_DEFAULTS.THRESHOLD;
					const error = validateAmount(value);
					setAmountError(error);
					if (!error) {
						handleSave(threshold, amount);
					}
				}}
				min={
					(autoTopupSettings?.threshold ?? AUTO_RELOAD_LIMITS.MIN) +
					AUTO_RELOAD_LIMITS.STEP
				}
				max={AUTO_RELOAD_LIMITS.MAX}
				step={AUTO_RELOAD_LIMITS.STEP}
				error={amountError}
			/>
			<Text size="xs" c="dimmed">
				Minimum $
				{(autoTopupSettings?.threshold ?? AUTO_RELOAD_LIMITS.MIN) +
					AUTO_RELOAD_LIMITS.STEP}
			</Text>
			<BillingInfoBanner />
		</Stack>
	);
}

/**
 * AutoReloadSettingsController Component
 *
 * A reusable component for managing AI token auto-reload settings.
 * This component handles:
 * - Toggling auto-reload on/off
 * - Configuring threshold and amount settings
 * - Displaying current auto-reload status
 *
 * Usage:
 * ```tsx
 * import { AutoReloadSettingsController } from './AutoReloadSettingsController';
 *
 * function MyComponent() {
 *   return (
 *     <AutoReloadSettingsController
 *       onSettingsChange={() => console.log('Settings changed')}
 *     />
 *   );
 * }
 * ```
 */
interface AutoReloadSettingsControllerProps {
	onSettingsChange?: () => void;
}

export function AutoReloadSettingsController({
	onSettingsChange,
}: AutoReloadSettingsControllerProps) {
	const { data: autoTopupSettings, refetch: refetchAutoTopupSettings } =
		useGetAiTokenAutoTopupSettings({});
	const { mutate: setAutoTopupSettings } = useSetAiTokenAutoTopupSettings({
		onSuccess: () => {
			refetchAutoTopupSettings();
			onSettingsChange?.();
		},
	});

	const handleEditReloadSettings = useCallback(() => {
		openModal({
			title: 'Auto reload credits',
			children: <EditReloadSettingsModal onSuccess={onSettingsChange} />,
		});
	}, [onSettingsChange]);

	const handleToggleAutoReload = useCallback(
		(enabled: boolean) => {
			if (enabled) {
				// Enable auto reload with default values
				setAutoTopupSettings({
					body: {
						enabled: true,
						threshold: AUTO_RELOAD_DEFAULTS.THRESHOLD,
						amount: AUTO_RELOAD_DEFAULTS.AMOUNT,
					},
				});
			} else {
				// Disable auto reload
				setAutoTopupSettings({
					body: {
						enabled: false,
						threshold:
							autoTopupSettings?.threshold || AUTO_RELOAD_DEFAULTS.THRESHOLD,
						amount: autoTopupSettings?.amount || AUTO_RELOAD_DEFAULTS.AMOUNT,
					},
				});
			}
		},
		[
			autoTopupSettings?.threshold,
			autoTopupSettings?.amount,
			setAutoTopupSettings,
		]
	);

	const formatCurrency = (amount: number) =>
		new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
		}).format(amount);

	return (
		<SettingsButton
			title="Auto reload credits"
			description={
				autoTopupSettings?.enabled
					? `Top up to ${formatCurrency(autoTopupSettings?.amount)} when balance reaches ${formatCurrency(autoTopupSettings?.threshold)}`
					: 'Auto reload is disabled'
			}
			leftComponent={<ResourceIcon iconName="refresh" />}
			onClick={handleEditReloadSettings}
		>
			<Group spacing="xs">
				{autoTopupSettings?.enabled && (
					<ActionIcon
						variant="subtle"
						size="sm"
						onClick={(e: React.MouseEvent) => {
							e.stopPropagation();
							handleEditReloadSettings();
						}}
					>
						<ResourceIcon iconName="settings" />
					</ActionIcon>
				)}
				<Switch
					checked={autoTopupSettings?.enabled || false}
					onChange={(event) => {
						handleToggleAutoReload(event.currentTarget.checked);
						if (event.currentTarget.checked) {
							handleEditReloadSettings();
						}
					}}
				/>
			</Group>
		</SettingsButton>
	);
}
